/**
 * @fileoverview Syndicator Showdown — Share Link Helpers
 * @module utils/shareLinks
 *
 * Pure helpers for building shareable URLs and triggering the native share
 * sheet / clipboard fallback. No Firestore or React dependencies here —
 * keep this file trivially unit-testable.
 */

const PRODUCTION_ORIGIN = 'https://syndicatorshowdown.com.au';

/**
 * Prefers the origin the app is actually running on (localhost, a preview
 * channel, the .web.app domain, or the custom domain) so links always work
 * in whichever environment generated them. Falls back to the canonical
 * production domain for non-browser contexts.
 *
 * @returns {string}
 */
export function getSiteOrigin() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return PRODUCTION_ORIGIN;
}

/**
 * @param {string} referralCode
 * @returns {string} e.g. https://syndicatorshowdown.com.au/share/invite/KELLY123
 */
export function buildInviteUrl(referralCode) {
  return `${getSiteOrigin()}/share/invite/${encodeURIComponent(referralCode)}`;
}

/**
 * @param {string} shareId
 * @returns {string} e.g. https://syndicatorshowdown.com.au/share/stable/abc123
 */
export function buildStableShareUrl(shareId) {
  return `${getSiteOrigin()}/share/stable/${encodeURIComponent(shareId)}`;
}

/**
 * Copies text to the clipboard, using the modern Clipboard API where
 * available and falling back to a hidden-textarea `execCommand` copy for
 * older/insecure (non-HTTPS) contexts.
 *
 * @param {string} text
 * @returns {Promise<boolean>} true if the copy succeeded
 */
export async function copyToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (err) {
    console.warn('[shareLinks] Clipboard API failed, falling back:', err.message);
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch (err) {
    console.error('[shareLinks] Clipboard fallback failed:', err);
    return false;
  }
}

/**
 * @returns {boolean} true if the Web Share API is available (mostly mobile browsers)
 */
export function canNativeShare() {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

/**
 * Triggers the OS-native share sheet.
 *
 * @param {{ title?: string, text?: string, url: string }} payload
 * @returns {Promise<boolean>} true if the share completed (false if unsupported,
 *   cancelled by the user, or it errored)
 */
export async function nativeShare(payload) {
  if (!canNativeShare()) return false;
  try {
    await navigator.share(payload);
    return true;
  } catch (err) {
    // AbortError fires when the user simply dismisses the share sheet — not a real error.
    if (err?.name !== 'AbortError') {
      console.error('[shareLinks] navigator.share error:', err);
    }
    return false;
  }
}
